<template>
  <div class="bc-root" :class="rootClasses">

    <!-- ── FAB (solo en modo standalone) ── -->
    <transition name="bc-fab-pop">
      <button
        v-if="!isOpen && !controlled"
        class="bc-fab"
        @click="openChat"
        aria-label="Abrir asistente IA"
      >
        <v-icon color="white" size="22">mdi-robot-outline</v-icon>
        <span v-if="hasUnread" class="bc-fab__dot" />
      </button>
    </transition>

    <!-- ── Ventana ── -->
    <transition name="bc-win-slide">
      <div
        v-if="isOpen"
        class="bc-window"
        :class="{ 'bc-window--fullscreen': isFullscreen }"
        :style="{ transformOrigin: anchor === 'left' ? 'bottom left' : 'bottom right' }"
        role="dialog"
        aria-label="Asistente IA"
      >
        <!-- Header -->
        <div class="bc-header">
          <div class="d-flex align-center" style="gap: 10px;">
            <div class="bc-avatar">
              <v-icon color="white" size="17">mdi-robot-outline</v-icon>
            </div>
            <div>
              <p class="bc-header__title mb-0">Asistente IA</p>
              <p class="bc-header__sub mb-0" v-if="contextLabel">
                <v-icon size="10" color="rgba(255,255,255,.55)" class="mr-1">mdi-link-variant</v-icon>
                {{ contextLabel }}
              </p>
              <p class="bc-header__sub mb-0" v-else>¿En qué puedo ayudarte?</p>
            </div>
          </div>
          <div class="d-flex align-center" style="gap: 4px;">
            <v-btn icon x-small @click="clearChat" title="Nueva conversación" class="bc-icon-btn">
              <v-icon size="15" color="rgba(255,255,255,.65)">mdi-refresh</v-icon>
            </v-btn>
            <!-- Botón expandir / contraer -->
            <v-btn icon x-small @click="toggleFullscreen" :title="isFullscreen ? 'Reducir' : 'Pantalla completa'" class="bc-icon-btn">
              <v-icon size="15" color="rgba(255,255,255,.65)">
                {{ isFullscreen ? 'mdi-arrow-collapse' : 'mdi-arrow-expand' }}
              </v-icon>
            </v-btn>
            <v-btn icon x-small @click="closeChat" title="Cerrar" class="bc-icon-btn">
              <v-icon size="15" color="rgba(255,255,255,.65)">mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- Mensajes -->
        <div class="bc-messages" ref="msgBox">
          <div
            v-for="(msg, i) in visibleMessages"
            :key="i"
            class="bc-msg"
            :class="'bc-msg--' + msg.role"
          >
            <template v-if="msg.role === 'assistant'">
              <div class="bc-msg__row">
                <div class="bc-msg__ai-dot">
                  <v-icon size="12" color="white">mdi-robot-outline</v-icon>
                </div>
                <div class="bc-msg__bubble" v-html="msg.content" />
              </div>
            </template>
            <template v-else>
              <div class="bc-msg__bubble" v-html="msg.content" />
            </template>
            <span class="bc-msg__time">{{ msg.time }}</span>
          </div>

          <!-- Typing -->
          <div v-if="isLoading" class="bc-msg bc-msg--assistant">
            <div class="bc-msg__row">
              <div class="bc-msg__ai-dot">
                <v-icon size="12" color="white">mdi-robot-outline</v-icon>
              </div>
              <div class="bc-msg__bubble bc-msg__bubble--typing">
                <span /><span /><span />
              </div>
            </div>
          </div>
        </div>

        <!-- Chips de sugerencia -->
        <div v-if="showChips" class="bc-chips">
          <button
            v-for="chip in suggestionChips"
            :key="chip"
            class="bc-chip"
            @click="sendChip(chip)"
          >{{ chip }}</button>
        </div>

        <!-- Input -->
        <div class="bc-input-area">
          <textarea
            ref="inputEl"
            v-model="draft"
            class="bc-input"
            placeholder="Escribe tu consulta…"
            rows="1"
            :disabled="isLoading"
            :style="{ pointerEvents: 'auto', opacity: isLoading ? '0.5' : '1' }"
            @keydown.enter.exact.prevent="sendMessage"
            @input="resizeInput"
            @click.stop
          />
          <button
            class="bc-send"
            :disabled="isLoading || !draft.trim()"
            :style="{ pointerEvents: 'auto' }"
            @click.stop="sendMessage"
            aria-label="Enviar"
          >
            <v-icon size="17" :color="(isLoading || !draft.trim()) ? '#bbb' : 'white'">mdi-send</v-icon>
          </button>
        </div>

      </div>
    </transition>
  </div>
</template>

<script>
import { chatWithTools } from "@/helpers/api/bot";

const TYPE_LABELS = {
  task: 'Tarea', tarea: 'Tarea',
  rdi: 'RDI',
  entrega: 'Entrega',
	adicional: 'Adicional'
}

export default {
  name: 'BotChat',

	props: {
		fixed: { type: Boolean, default: false },
		// Modo controlado: la apertura/cierre la maneja un componente padre
		// (ej. GlobalFloatingButtons) a través de la prop `open`.
		controlled: { type: Boolean, default: false },
		open: { type: Boolean, default: false },
		// Lado hacia el que se ancla/expande la ventana ('left' | 'right')
		anchor: { type: String, default: 'right' }
	},

  data: () => ({
    isOpen: false,
    isFullscreen: false,
    hasUnread: false,
    draft: '',
    // FIX: isLoading solo lo controla sendMessage/callApi,
    // nunca injectContextChange ni push directo
    isLoading: false,
    history: [],
    visibleMessages: [],
    contextObject: null,
    lastContextKey: null,
  }),

  computed: {
    // ── Stores ──────────────────────────────────────────────
		currentTarea()    {
			if(!this.$store.consultasStore.state.currentTarea) return null
			return {...this.$store.consultasStore.state.currentTarea, ...{element_type: 'tarea'}}
		},
		currentRDI()      {
			if(!this.$store.rdiStore.state.currentRDI) return null
			return {...this.$store.rdiStore.state.currentRDI, ...{element_type: 'rdi'}}
		},
		currentAdicional(){
			if(!this.$store.adicionalesStore.state.currentAdicional) return null
			return {...this.$store.adicionalesStore.state.currentAdicional, ...{element_type: 'adicional'}}
		},
		currentEntrega()  {
			if(!this.$store.entregaStore.state.currentEntrega) return null
			return {...this.$store.entregaStore.state.currentEntrega, ...{element_type: 'entrega'}}
		},

    // FIX: currentElement es computed puro — sin props, sin data —
    // así el watcher nunca mezcla ref de objeto con isLoading
    currentElement() {
      return this.currentTarea || this.currentRDI || this.currentAdicional || this.currentEntrega || null
    },

    selectedProject() {
      return this.$store.projectsStore.state.selectedProject
    },

    rootClasses() {
      const classes = []
      if (this.fixed) classes.push('bc-fixed')
      if (this.controlled) classes.push('bc-controlled-' + this.anchor)
      return classes
    },

    // ── Derivados del elemento ───────────────────────────────
    contextLabel() {
      const el = this.currentElement
      if (!el) return null
      return el.title || el.name || el.code || el.label || null
    },

    elementTypeName() {
      const el = this.currentElement
      if (!el) return 'elemento'
      return TYPE_LABELS[(el.element_type || '').toLowerCase()] || el.element_type || 'elemento'
    },

    // Clave estable: solo cambia cuando id cambia, no en cada re-render
    contextKey() {
      const el = this.currentElement
      if (!el || !el.id) return '__none__'
      return `${(el.type || 'x').toLowerCase()}-${el.id}`
    },

    projectId() {
      if (this.currentElement && this.currentElement.project_id) {
        return this.currentElement.project_id
      }
      return this.selectedProject ? this.selectedProject.id : null
    },

    showChips() {
      return this.visibleMessages.length > 0 &&
        !this.visibleMessages.some(m => m.role === 'user') &&
        !this.isLoading
    },

    suggestionChips() {
      if (this.currentElement) {
        return [
          `¿Cuál es el estado de ${this.elementTypeName.toLowerCase()}?`,
          'Muéstrame los detalles',
          'Preparar Resumen',
        ]
      }
      return ['¿En qué me puedes ayudar?', 'Estado del proyecto', 'Tareas pendientes']
    },
  },

  watch: {
    contextKey(newKey) {
      // Resetear contextObject enriquecido porque cambiamos de elemento
      this.contextObject = null

      if (!this.isOpen) {
        // Si el chat está cerrado, solo actualizamos la clave para el próximo open
        this.lastContextKey = newKey
        return
      }

      if (newKey === this.lastContextKey) return
      this.lastContextKey = newKey

      // FIX: injectContextChange es puro UI, nunca toca isLoading.
      // Si había una petición en vuelo la dejamos terminar normalmente.
      this.injectContextChange()
    },

    isOpen(val) {
      if (val) {
        this.$nextTick(() => {
          this.$refs.inputEl && this.$refs.inputEl.focus()
          this.scrollBottom()
        })
      }
    },

    // Modo controlado: un padre (GlobalFloatingButtons) decide cuándo abrir/cerrar
    open(val) {
      if (!this.controlled) return
      if (val) {
        this.isOpen = true
        this.hasUnread = false
        if (this.visibleMessages.length === 0) {
          this.lastContextKey = this.contextKey
          this.pushWelcome()
        }
      } else {
        this.isOpen = false
        this.isFullscreen = false
      }
    },
  },

  methods: {
    // ── UI ──────────────────────────────────────────────────

    openChat() {
      this.isOpen = true
      this.hasUnread = false
      if (this.visibleMessages.length === 0) {
        this.lastContextKey = this.contextKey
        this.pushWelcome()
      }
    },

    closeChat() {
      this.isOpen = false
      this.isFullscreen = false
      if (this.controlled) this.$emit('close')
    },

    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen
      this.$nextTick(() => this.scrollBottom())
    },

    clearChat() {
      this.history = []
      this.visibleMessages = []
      this.contextObject = null
      this.isLoading = false       // garantía: limpiar estado en caso de abort
      this.lastContextKey = this.contextKey
      this.pushWelcome()
    },

    resizeInput() {
      const el = this.$refs.inputEl
      if (!el) return
      el.style.height = 'auto'
      el.style.height = Math.min(el.scrollHeight, 108) + 'px'
    },

    scrollBottom() {
      this.$nextTick(() => {
        const c = this.$refs.msgBox
        if (c) c.scrollTop = c.scrollHeight
      })
    },

    fmt(text = '') {
      return text
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/`(.+?)`/g, '<code>$1</code>')
        .replace(/\n/g, '<br>')
    },

    ts() {
      return new Date().toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })
    },

    // ── Mensajes ─────────────────────────────────────────────

    // FIX: push es solo UI + historial — no toca isLoading nunca
    push(role, content) {
      this.visibleMessages.push({ role, content, time: this.ts() })
      // Los mensajes de contexto (sistema) no van al historial del backend
      if (role === 'user' || role === 'assistant') {
        this.history.push({ role, content })
      }
      this.scrollBottom()
    },

    pushWelcome() {
      const el = this.currentElement
      let text
      if (el) {
        const name = this.contextLabel
          ? `**${this.contextLabel}**`
          : `este ${this.elementTypeName.toLowerCase()}`
        text = `¡Hola! Soy tu asistente IA. 👋\n\nVeo que tienes abierto ${name} *(${this.elementTypeName})*.\n\nPuedo ayudarte con:\n• Consultar su estado o detalles\n• Responder preguntas sobre este elemento\n• Buscar información en el proyecto\n\n¿En qué te ayudo?`
      } else {
        text = `¡Hola! Soy tu asistente IA. 👋\n\nPuedo ayudarte con tareas, RDIs, entregas, visitas y más.\n\nAbre un elemento en la plataforma para que tenga contexto, o simplemente pregúntame lo que necesites. ¿En qué te ayudo?`
      }
      this.push('assistant', text)
    },

    // FIX: solo inserta un mensaje visual, NO modifica isLoading
    injectContextChange() {
      const el = this.currentElement
      const label = this.contextLabel ? `**${this.contextLabel}**` : 'un nuevo elemento'
      const msg = el
        ? `*Contexto actualizado:* Ahora estás viendo ${label} *(${this.elementTypeName})*. Puedo responder sobre este elemento o continuar con la conversación anterior.`
        : `*Contexto actualizado:* Ya no hay un elemento abierto. Puedo seguir ayudándote con consultas generales del proyecto.`
      // Insertar sin afectar el historial del backend (el watcher no es un turno de conversación)
      this.visibleMessages.push({ role: 'assistant', content: msg, time: this.ts() })
      this.scrollBottom()
    },

    sendChip(text) {
      this.draft = text
      this.$nextTick(() => this.sendMessage())
    },

    // ── API ──────────────────────────────────────────────────

    async sendMessage() {
      const text = this.draft.trim()
      if (!text || this.isLoading) return

      this.draft = ''
      this.$nextTick(() => {
        const el = this.$refs.inputEl
        if (el) el.style.height = 'auto'
      })

      this.push('user', text)
      this.isLoading = true

      try {
        await this.callApi()
      } catch {
        this.push('assistant', '⚠️ No pude conectarme con el asistente. Intenta de nuevo.')
      } finally {
        // FIX: el finally SIEMPRE corre, incluso si contextKey cambió
        // durante la petición. Nunca queda bloqueado.
        this.isLoading = false
        this.scrollBottom()
        this.$nextTick(() => {
          this.$refs.inputEl && this.$refs.inputEl.focus()
        })
      }
    },

    async callApi() {
      const payload = {
        project_id: this.projectId,
        messages: [...this.history],
        context: this.buildContext(),
      }

      const response = await chatWithTools(payload)

      if (!response || !response.success) {
        throw new Error(response?.message || 'Error desconocido')
      }

      const data = response.data

      // Si el backend cargó un elemento, lo acumulamos como contexto enriquecido
      if (data.loaded_element) {
        this.contextObject = data.loaded_element
      }

      const assistantText = data.content || data.message || data.text || ''
      if (assistantText) {
        this.push('assistant', assistantText)
      }
    },

    buildContext() {
      const ctx = {}

      // Prioridad: loaded_element del backend > currentElement del store
      if (this.contextObject) {
        ctx.context_object = this.contextObject
      } else if (this.currentElement) {
        ctx.context_object = this.currentElement
      }

      if (this.contextLabel) {
        ctx.context_label = this.contextLabel
      }

      // Sin system_prompt: lo maneja el backend
      return Object.keys(ctx).length ? ctx : undefined
    },
  },
}
</script>

<style scoped>
.bc-root {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  /* Aislar del v-card :disabled que Vuetify propaga como pointer-events:none */
  pointer-events: auto !important;
}

.bc-fixed{
  position: fixed;
  bottom: 10px;
  right: 10px;
  z-index: 9998;
}

/* Modo controlado: la ventana se posiciona sola (el FAB lo maneja el padre) */
.bc-controlled-left {
  position: fixed;
  bottom: 10px;
  left: 10px;
  z-index: 9998;
}
.bc-controlled-right {
  position: fixed;
  bottom: 10px;
  right: 10px;
  z-index: 9998;
}

/* ── FAB ── */
.bc-fab {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #1C3A7A;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 18px rgba(28, 58, 122, .45);
  transition: background .18s, transform .15s, box-shadow .18s;
  position: relative;
}
.bc-fab:hover {
  background: #2D4B95;
  transform: scale(1.06);
  box-shadow: 0 6px 24px rgba(28, 58, 122, .6);
}
.bc-fab__dot {
  position: absolute;
  top: 8px; right: 8px;
  width: 9px; height: 9px;
  border-radius: 50%;
  background: #ef4444;
  border: 2px solid white;
}

/* ── Ventana ── */
.bc-window {
  width: 370px;
  height: 510px;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 20px 60px rgba(0,0,0,.18), 0 4px 16px rgba(0,0,0,.1);
  border: 1px solid #E0E6F5;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  pointer-events: auto !important;
  /* Transición suave al expandir */
  transition: width .28s cubic-bezier(.16,1,.3,1),
              height .28s cubic-bezier(.16,1,.3,1),
              border-radius .28s cubic-bezier(.16,1,.3,1),
              bottom .28s cubic-bezier(.16,1,.3,1),
              right .28s cubic-bezier(.16,1,.3,1);
}

/* Fullscreen: ocupa toda la pantalla sobre el FAB */
.bc-window--fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw !important;
  height: 100vh !important;
  border-radius: 0 !important;
  z-index: 9999;
}

/* ── Header ── */
.bc-header {
  background: #1C3A7A;
  padding: 13px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}
.bc-avatar {
  width: 32px; height: 32px;
  border-radius: 50%;
  background: rgba(255,255,255,.15);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.bc-header__title {
  font-size: 13px; font-weight: 600; color: #fff; line-height: 1.2;
}
.bc-header__sub {
  font-size: 11px; color: rgba(255,255,255,.6); line-height: 1.3;
  display: flex; align-items: center;
  max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.bc-icon-btn {
  background: rgba(255,255,255,.08) !important;
  transition: background .15s !important;
}
.bc-icon-btn:hover { background: rgba(255,255,255,.2) !important; }

/* ── Mensajes ── */
.bc-messages {
  flex: 1;
  overflow-y: auto;
  padding: 14px 14px 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  scrollbar-width: thin;
  scrollbar-color: #E0E6F5 transparent;
}
.bc-messages::-webkit-scrollbar { width: 4px; }
.bc-messages::-webkit-scrollbar-thumb { background: #E0E6F5; border-radius: 2px; }

.bc-msg {
  display: flex; flex-direction: column;
  max-width: 86%;
}
.bc-msg--user  { align-self: flex-end;  align-items: flex-end; }
.bc-msg--assistant { align-self: flex-start; align-items: flex-start; }

.bc-msg__row { display: flex; align-items: flex-end; gap: 7px; }
.bc-msg__ai-dot {
  width: 22px; height: 22px;
  border-radius: 50%; background: #2D4B95;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; margin-bottom: 2px;
}

.bc-msg__bubble {
  padding: 8px 12px; border-radius: 10px;
  font-size: 12.5px; line-height: 1.6; word-break: break-word;
}
.bc-msg--user .bc-msg__bubble {
  background: #2D4B95; color: #fff; border-bottom-right-radius: 3px;
}
.bc-msg--assistant .bc-msg__bubble {
  background: #F3F6FB; color: #2c3352; border-bottom-left-radius: 3px;
}
/deep/ .bc-msg__bubble code {
  background: rgba(45,75,149,.1); padding: 1px 5px; border-radius: 4px;
  font-size: 11.5px; font-family: 'Fira Code', monospace; color: #1C3A7A;
}
/deep/ .bc-msg__bubble strong { color: inherit; }

.bc-msg__time { font-size: 10px; color: #aab0c0; margin-top: 3px; padding: 0 2px; }

/* Typing */
.bc-msg__bubble--typing {
  display: flex; gap: 5px; align-items: center; padding: 10px 14px !important;
}
.bc-msg__bubble--typing span {
  width: 6px; height: 6px; border-radius: 50%; background: #8b96b0;
  animation: bc-dot 1.2s ease-in-out infinite;
}
.bc-msg__bubble--typing span:nth-child(2) { animation-delay: .18s; }
.bc-msg__bubble--typing span:nth-child(3) { animation-delay: .36s; }

/* ── Chips ── */
.bc-chips {
  display: flex; flex-wrap: wrap; gap: 6px;
  padding: 0 14px 10px; flex-shrink: 0;
}
.bc-chip {
  font-size: 11.5px; color: #2D4B95; background: #EEF2FB;
  border: 1px solid #C5D0EA; border-radius: 20px;
  padding: 4px 11px; cursor: pointer;
  transition: background .15s, border-color .15s;
  white-space: nowrap; line-height: 1.4;
}
.bc-chip:hover { background: #dde5f7; border-color: #2D4B95; }

/* ── Input ── */
.bc-input-area {
  display: flex; align-items: flex-end; gap: 8px;
  padding: 10px 12px; border-top: 1px solid #F0F0F0;
  background: #fafbff; flex-shrink: 0;
  pointer-events: auto !important;
}
.bc-input {
  flex: 1; resize: none;
  border: 1px solid #E0E6F5; border-radius: 8px;
  background: #fff; color: #2c3352;
  font-size: 12.5px; font-family: inherit;
  padding: 8px 11px; line-height: 1.5; outline: none;
  min-height: 34px; max-height: 108px; overflow-y: auto;
  transition: border-color .18s;
  pointer-events: auto !important;
}
.bc-input::placeholder { color: #aab0c0; }
.bc-input:focus { border-color: #2D4B95; }
/* NO usar :disabled para opacar — lo manejamos via :style en el template */

.bc-send {
  width: 34px; height: 34px; border-radius: 8px;
  background: #2D4B95; border: none; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; transition: background .18s, transform .1s;
}
.bc-send:hover:not(:disabled) { background: #1C3A7A; transform: scale(1.05); }
.bc-send:disabled { background: #E0E6F5; cursor: default; }

/* ── Transiciones ventana ── */
.bc-fab-pop-enter-active { animation: bc-pop-in .22s cubic-bezier(.34,1.56,.64,1); }
.bc-fab-pop-leave-active { animation: bc-pop-in .15s ease reverse; }

.bc-win-slide-enter-active {
  animation: bc-win-in .26s cubic-bezier(.16,1,.3,1);
}
.bc-win-slide-leave-active {
  animation: bc-win-in .18s ease reverse;
}

/* ── Keyframes ── */
@keyframes bc-dot {
  0%, 80%, 100% { transform: scale(.55); opacity: .4; }
  40%           { transform: scale(1);   opacity: 1;  }
}
@keyframes bc-pop-in {
  from { transform: scale(.5); opacity: 0; }
  to   { transform: scale(1);  opacity: 1; }
}
@keyframes bc-win-in {
  from { transform: scale(.88) translateY(12px); opacity: 0; }
  to   { transform: scale(1)   translateY(0);    opacity: 1; }
}

/* ── Responsive ── */
@media (max-width: 420px) {
  .bc-root { bottom: 12px; right: 12px; }
  .bc-controlled-left { bottom: 12px; left: 12px; }
  .bc-controlled-right { bottom: 12px; right: 12px; }
  .bc-window:not(.bc-window--fullscreen) {
    width: calc(100vw - 24px);
    height: 72vh;
  }
}
</style>

<!-- Estilo global NO scoped: necesario para que pointer-events
     no sea anulado por v-card[disabled] o v-dialog de Vuetify
     cuando el componente está montado dentro de uno de ellos. -->
<style>
.bc-root,
.bc-root * {
  pointer-events: auto!important;
}
</style>